import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

export default function Footer() {
  const { t, i18n } = useTranslation();

  const toggleLanguage = () => {
    const newLang = i18n.language === 'en' ? 'ua' : 'en';
    i18n.changeLanguage(newLang);
    localStorage.setItem('language', newLang);
  };

  const navigation = [
    { name: t('nav_veterans'), href: '/veterans' },
    { name: t('nav_families'), href: '/families' },
    { name: t('nav_outreach'), href: '/outreach' },
    { name: t('nav_about'), href: '/about' },
    { name: t('nav_contact'), href: '/contact' },
  ];

  return (
    <footer className="bg-stone-800 text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* Column 1 - Brand */}
          <div className="lg:col-span-1">
            <div className="flex items-center space-x-3 mb-4">
              <img 
                src="https://static.readdy.ai/image/e7605311edff4188dbd664db0f9809d3/f17a003f3d23c1164cef4e6ee6113553.png" 
                alt="Valhalla on Earth Logo" 
                className="h-8 w-8"
              />
              <span className="text-xl font-medium">Valhalla on Earth</span>
            </div>
            <p className="text-stone-300 text-sm leading-relaxed">
              {t('footer_tagline')}
            </p>
          </div>

          {/* Column 2 - Navigation */}
          <div>
            <h3 className="text-sm font-medium uppercase tracking-wide mb-4">
              {t('footer_navigate')}
            </h3>
            <ul className="space-y-3">
              {navigation.map((item) => (
                <li key={item.name}>
                  <Link
                    to={item.href}
                    className="text-stone-300 hover:text-white text-sm transition-colors duration-200 hover:translate-x-1 inline-block"
                  >
                    {item.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Column 3 - Connect */}
          <div>
            <h3 className="text-sm font-bold text-stone-800 uppercase tracking-wide mb-4">
              {t('footer_connect')}
            </h3>
            <div className="space-y-3">
              {/* Email */}
              <a 
                href="mailto:valhalla.rehab@gmail.com" 
                className="flex items-center space-x-3 group"
              >
                <div className="w-8 h-8 bg-stone-100 rounded-lg flex items-center justify-center group-hover:bg-amber-100 transition-colors duration-200">
                  <i className="ri-mail-line text-stone-600 group-hover:text-amber-600"></i>
                </div>
                <span className="text-stone-600 text-sm group-hover:text-amber-600 transition-colors duration-200">
                  valhalla.rehab@gmail.com
                </span>
              </a>

              {/* Telegram */}
              <a 
                href={t('contact_telegram_link')}
                target="_blank" 
                rel="noopener noreferrer"
                className="flex items-center space-x-3 group"
              >
                <div className="w-8 h-8 bg-stone-100 rounded-lg flex items-center justify-center group-hover:bg-amber-100 transition-colors duration-200">
                  <i className="ri-telegram-line text-stone-600 group-hover:text-amber-600"></i>
                </div>
                <span className="text-stone-600 text-sm group-hover:text-amber-600 transition-colors duration-200">
                  {t('contact_telegram')}
                </span>
              </a>

              {/* Facebook */}
              <a 
                href={t('contact_facebook_link')}
                target="_blank" 
                rel="noopener noreferrer"
                className="flex items-center space-x-3 group"
              >
                <div className="w-8 h-8 bg-stone-100 rounded-lg flex items-center justify-center group-hover:bg-amber-100 transition-colors duration-200">
                  <i className="ri-facebook-line text-stone-600 group-hover:text-amber-600"></i>
                </div>
                <span className="text-stone-600 text-sm group-hover:text-amber-600 transition-colors duration-200">
                  Valhalla Rehab
                </span>
              </a>

              {/* Instagram */}
              <a 
                href={t('contact_instagram_link')}
                target="_blank" 
                rel="noopener noreferrer"
                className="flex items-center space-x-3 group"
              >
                <div className="w-8 h-8 bg-stone-100 rounded-lg flex items-center justify-center group-hover:bg-amber-100 transition-colors duration-200">
                  <i className="ri-instagram-line text-stone-600 group-hover:text-amber-600"></i>
                </div>
                <span className="text-stone-600 text-sm group-hover:text-amber-600 transition-colors duration-200">
                  valhallaonearth5
                </span>
              </a>
            </div>
          </div>

          {/* Column 4 - Emergency Note */}
          <div>
            <h3 className="text-sm font-medium uppercase tracking-wide text-amber-500 mb-4">
              {t('footer_emergency_title')}
            </h3>
            <p className="text-stone-300 text-sm leading-relaxed mb-6">
              {t('footer_emergency_text')}
            </p>
            
            {/* Language Toggle Repeated */}
            <button
              onClick={toggleLanguage}
              className="flex items-center h-10 px-3 rounded-full border border-stone-600 bg-stone-700 hover:bg-stone-600 transition-colors duration-200"
            >
              <span className={`px-2 py-1 text-sm font-medium rounded-full transition-all duration-200 ${
                i18n.language === 'en' ? 'bg-white text-stone-800' : 'text-stone-300'
              }`}>
                {t('language_en')}
              </span>
              <span className="mx-1 text-stone-400">|</span>
              <span className={`px-2 py-1 text-sm font-medium rounded-full transition-all duration-200 ${
                i18n.language === 'ua' ? 'bg-white text-stone-800' : 'text-stone-300'
              }`}>
                {t('language_ua')}
              </span>
            </button>
          </div>
        </div>

        {/* Footer Bottom */}
        <div className="border-t border-stone-700 mt-12 pt-8 text-center">
          <p className="text-stone-400 text-sm">
            {t('footer_copyright')} • {' '}
            <a 
              href="https://readdy.ai/?ref=logo" 
              target="_blank" 
              rel="noopener noreferrer"
              className="hover:text-white transition-colors duration-200"
            >
              Powered by Readdy
            </a>
          </p>
        </div>
      </div>
    </footer>
  );
}